import { useEffect, useState, ReactNode } from "react";
import globalSearchClient from "@/apis/globalSearchClient";
import GlobalUtils from "@/utils/GlobalUtils";
import SearchItem from "./SearchItem";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import AwesomeIcon from "@icons/AwesomeIcon";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntTargetIcon from "@icons/SntTargetIcon";
import SntSearchIcon from "@icons/SntSearchIcon";
import SntInOrientationIcon from "@icons/SntInOrientationIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import { DeviceHomeRoute } from "@/cleanup/utils/routesUtil/routesUtil";
import GeozoneImageDefault from "@images/default/geozone_default.svg";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { UserPermission } from "@/types/generated/UserPermission";
import {
  SearchIndexItem,
  SearchHistoryItem,
  SearchItemData,
  SearchDataCategory,
} from "./types";

interface SearchPanelProps {
  searchText: string | null;
  closePopup: () => void;
}

function SearchPanel({ searchText, closePopup }: SearchPanelProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [isRecent, setIsRecent] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [searchData, setSearchData] = useState<SearchDataCategory[]>();

  useEffect(() => {
    setQuery(searchText);
  }, [searchText]);

  useDebouncedEffect(() => loadData(query), 500, [query]);

  const loadData = (search: string | null) => {
    if (search) {
      setSearchData([]);
      setIsRecent(false);
      globalSearchClient.search(search).then(({ data }: { data: SearchIndexItem[] }) => {
        drawDataSearch(data);
      });
    } else if (!search) {
      setSearchData([]);
      setIsRecent(true);
      setIsNotFound(false);
      globalSearchClient
        .getSearchHistory()
        .then(({ data }: { data: SearchHistoryItem[] }) => {
          drawRecentViewed(data);
        });
    }
  };

  const drawDataSearch = (data: SearchIndexItem[]) => {
    const map: { [key: string]: SearchIndexItem[] } = {};

    if (data.length === 0) {
      setIsNotFound(true);
      return;
    }
    setIsNotFound(false);
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      if (map[item.category]) {
        const arr = map[item.category];
        arr.push(item);
        map[item.category] = arr;
      } else {
        const arr1: SearchIndexItem[] = [];
        arr1.push(item);
        map[item.category] = arr1;
      }
    }
    const items = Object.entries(map);
    const listData: { name: string; dataSearch: SearchIndexItem[] }[] = [];
    for (let index = 0; index < items.length; index++) {
      const element = items[index];
      const categorySearch = { name: element[0], dataSearch: element[1] };
      listData.push(categorySearch);
    }

    const searchList: { name: string; dataSearch: SearchIndexItem[] }[] = [];
    for (let index = 0; index < listData.length; index++) {
      const element = listData[index];
      searchList.push(element);
    }
    const datasHandle: SearchDataCategory[] = [];
    for (let i = 0; i < searchList.length; i++) {
      const value = searchList[i];
      const searchItems: SearchItemData[] = [];
      for (let index = 0; index < value?.dataSearch.length; index++) {
        const element = value?.dataSearch[index];
        const obj = getData(element);
        searchItems.push(obj);
      }
      const searchItem = { name: value.name, listItem: searchItems };
      datasHandle.push(searchItem);
    }
    setSearchData(datasHandle);
  };

  const drawRecentViewed = (data: SearchHistoryItem[]) => {
    const searchList: SearchItemData[] = [];
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      const obj = getData(item.searchIndex);
      searchList.push(obj);
    }
    const searchDatas: SearchDataCategory[] = [];
    searchDatas.push({
      name: translate(LanguageKey.v41_recent_viewed_key),
      listItem: searchList,
    });
    setSearchData(searchDatas);
  };

  const getData = (data: SearchIndexItem): SearchItemData => {
    let link = "";
    let icon_class: ReactNode = "";
    let category = "";
    const element: ReactNode = null;
    let entityType = "";
    let resourceType = "image";

    const _suffix = "?";

    // Ensure data has title field (API might return it)
    const enrichedData: SearchIndexItem = {
      ...data,
      title: data.title || "",
    };

    switch (data.category) {
      case "Asset":
        link = DeviceHomeRoute.getUpdatePage({
          id: data.entityId,
        });

        icon_class = (
          <img
            src="/images/device/tracker_default.svg"
            alt=""
            style={{ width: 30, height: 30 }}
          />
        );
        category = translate(LanguageKey.assets_key);
        entityType = "device";
        break;

      case "AlertRule":
        link =
          "/manage_alerts_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <IcomoonIcon icon="warning" size="30"></IcomoonIcon>;
        category = translate(LanguageKey.sidebar_admin_alerts_key);
        break;

      case "Geozone":
        link =
          "/geozone_individual" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = (
          <img
            src={GeozoneImageDefault}
            alt=""
            style={{ width: 30, height: 30 }}
          />
        );
        category = translate(LanguageKey.geozones_key);
        entityType = "geozone";
        break;

      case "Infrastructure":
        link =
          "/infrastructures_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <SntTargetIcon size={30}></SntTargetIcon>;
        category = translate(LanguageKey.infrastructure_key);
        break;

      case "User":
        link =
          "/users_admin_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <IcomoonIcon icon="users" size="30"></IcomoonIcon>;
        category = translate(LanguageKey.sidebar_admin_users_key);
        entityType = "user";
        resourceType = "avatar";
        break;

      case "Organization":
        if (hasPermission(UserPermission.ORGANIZATION_MANAGE)) {
          link =
            "/organization_update" + _suffix + "type=edit&org=" + data.entityId;
        } else {
          link =
            "/organization_update" + _suffix + "type=view&org=" + data.entityId;
        }
        icon_class = <AwesomeIcon icon="user-group" size="2xl"></AwesomeIcon>;
        category = translate(LanguageKey.organization_key);
        break;

      case "Partner":
        link = "/partners_update" + _suffix + "type=view&id=" + data.entityId;
        icon_class = <SntPartnersIcon size={30} />;
        category = translate(LanguageKey.partner_partners_key);
        break;
      case "Sensor":
        link =
          "/ble_sensor_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <SntInOrientationIcon size={30} />;
        category = translate(LanguageKey.sidebar_ex_ble_sensor_title_key);
        break;
      default:
        break;
    }

    let img: ReactNode = "";
    if (data.iconPath) {
      img = (
        <img
          style={{ float: "left", width: 30, height: 30, marginRight: 10 }}
          src={GlobalUtils.getFile(
            entityType,
            data.entityId,
            resourceType,
            data.iconPath,
            "small"
          )}
          alt=""
        />
      );
    } else {
      img = (
        <span style={{ float: "left", marginRight: 10, paddingTop: 5 }}>
          {icon_class}
        </span>
      );
    }

    return {
      link: link,
      img: img,
      category: category,
      element: element,
      data: enrichedData,
    };
  };

  return (
    <div id="search-panel">
      <div className="d-md-none">
        <div className="gb-search-input">
          <input
            id="search-input"
            type="search"
            className="form-control"
            placeholder="Search…"
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            style={{ position: "relative" }}
          />
          <SntSearchIcon />
        </div>
      </div>
      <ul className="gb-search-item snt-scrollbar">
        {!isNotFound &&
          searchData
            ?.filter(
              (item) =>
                item.name !== "Geozone" ||
                (hasFeature(FeatureKey.geozone_db) &&
                  hasPermission(UserPermission.MENU_HOME_GEOZONE_OVERVIEW))
            )
            .map((item, index) => (
              <SearchItem
                key={index}
                dataSearch={item}
                closePopup={() => closePopup()}
                isRecent={isRecent}
                strSearch={searchText || undefined}
              />
            ))}
        <div
          className="not-found text-center"
          style={{
            paddingTop: 30,
            display: `${isNotFound ? "block" : "none"}`,
            color: "#424B59",
          }}
        >
          <SntSearchIcon size={60} />
          <div style={{ fontSize: 16, fontWeight: "bold" }}>
            {translate(LanguageKey.v41_no_matching_search_results_key)}
          </div>
          <div className="fs-6">
            {translate(LanguageKey.v41_try_again_using_more_general_search_terms_key)}
          </div>
        </div>
      </ul>
    </div>
  );
}

export default SearchPanel;
